import React from 'react';

import { ActionCluster, CompactMetric, DisclosurePanel } from '../components/AppShellPrimitives';
import CopyValue from '../components/CopyValue';
import { EvidencePostureChip, formatEvidenceDateTime } from '../evidencePosture';
import { reliabilityVerdictChipClassName, rollupReliabilityHelperCopy, rollupReliabilitySummaryItems } from '../reliability';

function defaultPortfolioSummary() {
  return {
    active_finding_count: 0,
    workflows_requiring_validation_count: 0,
    last_refreshed_at: null,
    evidence_posture: { state: 'unknown', last_analysed_at: null },
    reliability_summary: {
      summary_label: 'Runtime reliability is still being established for this system.',
      verdict_counts: {
        operationally_unstable: 0,
        operationally_stable_validation_needed: 0,
        reliable_for_covered_scenarios: 0,
        low_confidence: 0,
      },
      workflows_requiring_validation_count: 0,
      low_confidence_workflow_count: 0,
    },
  };
}

export default function SystemContextStrip({
  selectedSystem,
  activeFindingCountOverride = null,
  validationNeededCountOverride = null,
  validationMetricLabel = 'Validation needed',
  currentRouteName = '',
}) {
  const portfolioSummary = selectedSystem?.portfolio_summary || defaultPortfolioSummary();
  const reliabilitySummary = portfolioSummary.reliability_summary || defaultPortfolioSummary().reliability_summary;
  const activeFindingCount = activeFindingCountOverride ?? portfolioSummary.active_finding_count ?? 0;
  const workflowValidationNeededCount = portfolioSummary.workflows_requiring_validation_count || 0;
  const validationNeededCount = validationNeededCountOverride ?? workflowValidationNeededCount;
  const nextStep =
    activeFindingCount > 0
      ? {
          label: 'Review findings',
          href: `#/systems/${selectedSystem.id}/findings`,
          currentRoutes: ['findings-list'],
          helper: `${activeFindingCount} ${activeFindingCount === 1 ? 'finding is' : 'findings are'} waiting for triage.`,
        }
      : workflowValidationNeededCount > 0
        ? {
            label: 'Review workflows',
            href: `#/systems/${selectedSystem.id}/workflows`,
            currentRoutes: ['workflows-list'],
            helper: `${workflowValidationNeededCount} ${workflowValidationNeededCount === 1 ? 'workflow needs' : 'workflows need'} validation context.`,
          }
        : {
            label: 'Compare outcome',
            href: `#/systems/${selectedSystem.id}/compare`,
            currentRoutes: ['compare'],
            helper: 'Use compare when post-fix runtime evidence is available.',
          };
  const nextStepIsCurrent = nextStep.currentRoutes.includes(currentRouteName);
  const coverageBoardIsCurrent = currentRouteName === 'runtime-coverage';

  return (
    <section className="panel compact-system-context" aria-label="Current system">
      <div className="compact-system-context-main">
        <div className="compact-system-title">
          <p className="detail-kicker">Current system</p>
          <h2>{selectedSystem.name}</h2>
          <p className="panel-copy">
            {reliabilitySummary.summary_label || selectedSystem.description || 'Review what needs attention next.'}
          </p>
        </div>
        <CompactMetric label="Active findings" value={activeFindingCount} />
        <CompactMetric label={validationMetricLabel} value={validationNeededCount} />
        <div className="compact-metric compact-metric-muted">
          <span>Freshness</span>
          <EvidencePostureChip posture={portfolioSummary.evidence_posture} />
        </div>
      </div>

      <div className="system-next-step">
        <div>
          <span className="summary-card-label">Next step</span>
          <strong>{nextStep.label}</strong>
          <p>{nextStep.helper}</p>
        </div>
        <ActionCluster align="end">
          {nextStepIsCurrent ? (
            <span className="system-next-step-current" aria-label={`${nextStep.label} is the current view`}>
              Current view
            </span>
          ) : (
            <a className="primary-button action-link-button" href={nextStep.href}>
              {nextStep.label}
            </a>
          )}
          {!coverageBoardIsCurrent ? (
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/runtime-coverage`}>
              Coverage board
            </a>
          ) : null}
        </ActionCluster>
      </div>

      <DisclosurePanel title="System details" summaryMeta="Technical context">
        <div className="system-technical-grid">
          <CopyValue label="System ID" value={selectedSystem.id} />
          <div className="detail-card">
            <span className="summary-card-label">Last analysed</span>
            <strong>{formatEvidenceDateTime(portfolioSummary.evidence_posture?.last_analysed_at)}</strong>
          </div>
          <div className="detail-card">
            <span className="summary-card-label">Snapshot refreshed</span>
            <strong>{formatEvidenceDateTime(portfolioSummary.last_refreshed_at)}</strong>
          </div>
        </div>
        <p className="overview-note">{rollupReliabilityHelperCopy}</p>
        <div className="chip-row">
          {rollupReliabilitySummaryItems(reliabilitySummary).map((item) => (
            <span
              aria-label={item.accessibleLabel}
              className={reliabilityVerdictChipClassName(item.key)}
              key={item.key}
              title={item.accessibleLabel}
            >
              {item.label} {item.count}
            </span>
          ))}
        </div>
      </DisclosurePanel>
    </section>
  );
}
